/**
 * AIImportWizard
 * Workflow en 3 étapes pour l'import de produits via AI
 * Étape 1: Upload du fichier
 * Étape 2: Validation et édition des données
 * Étape 3: Confirmation et import final
 */

import { useState, useCallback } from 'react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Alert } from '../ui/Alert';
import { Badge } from '../ui/Badge';
import { Input } from '../ui/Input';
import { useTenant } from '../../contexts/TenantContext';
import { api } from '../../services/api';
import { logger } from '../../utils/logger';

interface ParsedProduct {
  id: string;
  name: string;
  price: number;
  barcode?: string;
  category?: string;
  tax_rate?: number;
  stock_quantity?: number;
  description?: string;
  confidence?: number; // Score de confiance AI (0-100)
  issues?: string[]; // Problèmes détectés
  origin_country?: string; // Pays d'origine
  organic_certifier_name?: string; // Certificateur bio
  unit?: string; // Unité (kg, pièce, etc.)
  product_code?: string; // Code produit
}

interface AIImportWizardProps {
  onComplete?: () => void;
  onCancel?: () => void;
}

export default function AIImportWizard({ onComplete, onCancel }: AIImportWizardProps) {
  const { tenant } = useTenant();
  const [currentStep, setCurrentStep] = useState(1);
  const [file, setFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);
  const [parsedProducts, setParsedProducts] = useState<ParsedProduct[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Étape 1: Upload du fichier
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
    }
  }, []);

  const handleFileDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
      setError(null);
    }
  }, []);

  const handleParse = async () => {
    if (!file) return;
    if (!tenant?.id) {
      setError('Tenant non identifié. Veuillez vous reconnecter.');
      return;
    }

    logger.log('[AIImportWizard] Starting parse with tenant:', tenant.id);
    logger.log('[AIImportWizard] File:', file.name, file.type, file.size);

    setParsing(true);
    setError(null);

    try {
      // Utiliser le client API qui gère automatiquement le header X-Tenant-ID
      const data = await api.uploadAndParse(file) as any;

      logger.log('[AIImportWizard] Upload successful:', data);

      // La réponse contient { delivery_id, supplier, line_count, confidence_score }
      if (!data.delivery_id) {
        throw new Error('Pas de delivery_id dans la réponse');
      }

      // Récupérer les lignes de la livraison
      const lines = await api.getDeliveryLines(tenant.id, data.delivery_id);

      // Convertir les lignes de livraison en produits pour l'affichage
      const productsWithIds = lines.map((line: any, i: number) => ({
        id: line.id || `temp-${i}`,
        name: line.normalized_name || line.raw_name || line.name,
        price: line.unit_price_ttc_cents ? line.unit_price_ttc_cents / 100 : 0,
        barcode: line.barcode,
        product_code: line.product_code,
        stock_quantity: line.quantity,
        unit: line.unit,
        tax_rate: line.tax_rate || line.tva || 21,
        origin_country: line.origin_country,
        organic_certifier_name: line.organic_certifier_name,
        category: line.category_hint,
        confidence: data.confidence_score || 100,
        issues: line.status === 'pending' && data.confidence_score < 70 ? ['Confiance faible - À vérifier'] : []
      }));

      setParsedProducts(productsWithIds);
      setSelectedProducts(new Set(productsWithIds.map((p: ParsedProduct) => p.id)));
      setCurrentStep(2);
    } catch (err) {
      logger.error('Parse error:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'analyse du fichier. Veuillez réessayer.');
    } finally {
      setParsing(false);
    }
  };

  // Étape 2: Validation et édition
  const handleToggleProduct = (id: string) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedProducts(newSelected);
  };

  const handleProductEdit = (id: string, field: string, value: any) => {
    setParsedProducts(products =>
      products.map(p =>
        p.id === id ? { ...p, [field]: value } : p
      )
    );
  };

  // Étape 3: Import final
  const handleImport = async () => {
    if (!tenant?.id) {
      setError('Tenant non identifié. Veuillez vous reconnecter.');
      return;
    }

    setImporting(true);
    setError(null);

    try {
      const productsToImport = parsedProducts.filter(p => selectedProducts.has(p.id));

      // Créer les produits via l'API
      const results = await api.bulkCreateProducts(productsToImport);

      logger.log('[AIImportWizard] Import results:', results);

      if (results.failed > 0) {
        logger.warn('[AIImportWizard] Some products failed to import:', results.errors);
      }

      setCurrentStep(3);
    } catch (err) {
      logger.error('Import error:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'import. Veuillez réessayer.');
    } finally {
      setImporting(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <StepUpload
            file={file}
            parsing={parsing}
            error={error}
            onFileSelect={handleFileSelect}
            onFileDrop={handleFileDrop}
            onParse={handleParse}
            onCancel={onCancel}
          />
        );
      case 2:
        return (
          <StepValidation
            products={parsedProducts}
            selectedProducts={selectedProducts}
            onToggleProduct={handleToggleProduct}
            onProductEdit={handleProductEdit}
            onBack={() => setCurrentStep(1)}
            onImport={handleImport}
            importing={importing}
            error={error}
          />
        );
      case 3:
        return (
          <StepConfirmation
            productsCount={selectedProducts.size}
            onComplete={onComplete}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Progress indicator */}
      <div className="flex items-center gap-2">
        {[1, 2, 3].map((step) => (
          <div key={step} className="flex items-center flex-1">
            <div
              className={`h-2 flex-1 rounded ${
                step <= currentStep ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            />
            {step < 3 && <div className="w-2" />}
          </div>
        ))}
      </div>

      {renderStep()}
    </div>
  );
}

// === ÉTAPE 1: Upload ===
function StepUpload({
  file,
  parsing,
  error,
  onFileSelect,
  onFileDrop,
  onParse,
  onCancel
}: {
  file: File | null;
  parsing: boolean;
  error: string | null;
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFileDrop: (e: React.DragEvent) => void;
  onParse: () => void;
  onCancel?: () => void;
}) {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          Étape 1 : Chargez votre fichier
        </h3>
        <p className="text-sm text-gray-600">
          Formats supportés : CSV, Excel (.xlsx, .xls), PDF
        </p>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {/* Drop zone */}
      <div
        onDrop={onFileDrop}
        onDragOver={(e) => e.preventDefault()}
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          file
            ? 'border-green-300 bg-green-50'
            : 'border-gray-300 hover:border-blue-400 bg-gray-50'
        }`}
      >
        {file ? (
          <div>
            <div className="text-4xl mb-3">📄</div>
            <p className="font-medium text-gray-900">{file.name}</p>
            <p className="text-sm text-gray-500 mt-1">
              {(file.size / 1024).toFixed(1)} KB
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => document.getElementById('file-input')?.click()}
              className="mt-3"
            >
              Changer de fichier
            </Button>
          </div>
        ) : (
          <div>
            <div className="text-4xl mb-3">📁</div>
            <p className="text-gray-700 mb-2">
              Glissez-déposez votre fichier ici
            </p>
            <p className="text-sm text-gray-500 mb-4">ou</p>
            <Button
              variant="outline"
              onClick={() => document.getElementById('file-input')?.click()}
            >
              Parcourir les fichiers
            </Button>
          </div>
        )}
        <input
          id="file-input"
          type="file"
          accept=".csv,.xlsx,.xls,.pdf"
          onChange={onFileSelect}
          className="hidden"
        />
      </div>

      {/* Info card */}
      <Card className="bg-blue-50 border-blue-200">
        <div className="flex gap-3">
          <div className="text-xl">💡</div>
          <div className="flex-1 text-sm">
            <p className="font-semibold text-blue-900 mb-1">
              Notre AI détecte automatiquement :
            </p>
            <ul className="text-blue-700 space-y-1">
              <li>• Nom des produits et descriptions</li>
              <li>• Prix TTC et HT</li>
              <li>• Codes-barres et références</li>
              <li>• Catégories et TVA</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* Actions */}
      <div className="flex gap-3">
        {onCancel && (
          <Button variant="outline" onClick={onCancel}>
            Annuler
          </Button>
        )}
        <Button
          variant="primary"
          onClick={onParse}
          disabled={!file || parsing}
          className="flex-1"
        >
          {parsing ? 'Analyse en cours...' : 'Analyser le fichier'}
        </Button>
      </div>
    </div>
  );
}

// === ÉTAPE 2: Validation ===
function StepValidation({
  products,
  selectedProducts,
  onToggleProduct,
  onProductEdit,
  onBack,
  onImport,
  importing,
  error
}: {
  products: ParsedProduct[];
  selectedProducts: Set<string>;
  onToggleProduct: (id: string) => void;
  onProductEdit: (id: string, field: string, value: any) => void;
  onBack: () => void;
  onImport: () => void;
  importing: boolean;
  error: string | null;
}) {
  const [editingId, setEditingId] = useState<string | null>(null);

  const stats = {
    total: products.length,
    selected: selectedProducts.size,
    highConfidence: products.filter(p => (p.confidence || 0) >= 90).length,
    withIssues: products.filter(p => (p.issues?.length || 0) > 0).length
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          Étape 2 : Vérifiez et corrigez les données
        </h3>
        <p className="text-sm text-gray-600">
          {stats.selected} produit(s) sélectionné(s) sur {stats.total} détecté(s)
        </p>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="text-center">
          <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
          <div className="text-xs text-gray-600">Détectés</div>
        </Card>
        <Card className="text-center">
          <div className="text-2xl font-bold text-green-600">{stats.highConfidence}</div>
          <div className="text-xs text-gray-600">Haute confiance</div>
        </Card>
        <Card className="text-center">
          <div className="text-2xl font-bold text-orange-600">{stats.withIssues}</div>
          <div className="text-xs text-gray-600">À vérifier</div>
        </Card>
      </div>

      {/* Products list */}
      <div className="max-h-96 overflow-y-auto space-y-2 border rounded-lg p-3 bg-gray-50">
        {products.map((product) => {
          const isSelected = selectedProducts.has(product.id);
          const isEditing = editingId === product.id;
          const confidence = product.confidence || 100;
          const hasIssues = (product.issues?.length || 0) > 0;

          return (
            <Card
              key={product.id}
              className={`transition-all ${
                isSelected ? 'ring-2 ring-blue-500' : 'opacity-60'
              }`}
            >
              <div className="flex items-start gap-3">
                {/* Checkbox */}
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => onToggleProduct(product.id)}
                  className="mt-1 h-5 w-5 text-blue-600 rounded"
                />

                {/* Content */}
                <div className="flex-1 min-w-0">
                  {isEditing ? (
                    <div className="space-y-2">
                      <Input
                        value={product.name}
                        onChange={(e) => onProductEdit(product.id, 'name', e.target.value)}
                        placeholder="Nom du produit"
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          type="number"
                          step="0.01"
                          value={product.price}
                          onChange={(e) => onProductEdit(product.id, 'price', parseFloat(e.target.value))}
                          placeholder="Prix"
                        />
                        <Input
                          value={product.barcode || ''}
                          onChange={(e) => onProductEdit(product.id, 'barcode', e.target.value)}
                          placeholder="Code-barres"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => setEditingId(null)}
                        >
                          Valider
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingId(null)}
                        >
                          Annuler
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 truncate">
                            {product.name}
                          </h4>
                          <div className="flex items-center gap-2 mt-1 text-sm text-gray-600">
                            <span className="font-semibold">{product.price.toFixed(2)}€</span>
                            {product.barcode && (
                              <span className="text-xs">• {product.barcode}</span>
                            )}
                            {product.category && (
                              <Badge variant="secondary" size="sm">
                                {product.category}
                              </Badge>
                            )}
                          </div>
                        </div>

                        {/* Confidence & Actions */}
                        <div className="flex items-center gap-2">
                          <Badge
                            variant={confidence >= 90 ? 'success' : confidence >= 70 ? 'warning' : 'error'}
                            size="sm"
                          >
                            {confidence}%
                          </Badge>
                          <button
                            onClick={() => setEditingId(product.id)}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            Modifier
                          </button>
                        </div>
                      </div>

                      {/* Issues */}
                      {hasIssues && (
                        <div className="mt-2 text-xs text-orange-700 bg-orange-50 rounded px-2 py-1">
                          ⚠️ {product.issues?.join(', ')}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack}>
          Retour
        </Button>
        <Button
          variant="primary"
          onClick={onImport}
          disabled={selectedProducts.size === 0 || importing}
          className="flex-1"
        >
          {importing
            ? 'Import en cours...'
            : `Importer ${selectedProducts.size} produit(s)`}
        </Button>
      </div>
    </div>
  );
}

// === ÉTAPE 3: Confirmation ===
function StepConfirmation({
  productsCount,
  onComplete
}: {
  productsCount: number;
  onComplete?: () => void;
}) {
  return (
    <div className="text-center space-y-6 py-8">
      <div className="text-6xl">✅</div>
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          Import réussi !
        </h3>
        <p className="text-gray-600">
          {productsCount} produit(s) ont été importés avec succès dans votre catalogue.
        </p>
      </div>

      <div className="flex flex-col gap-3 max-w-sm mx-auto">
        <Button variant="primary" onClick={onComplete}>
          Terminer
        </Button>
        <button
          onClick={() => window.location.href = '/products'}
          className="text-sm text-blue-600 hover:text-blue-800 underline"
        >
          Voir les produits importés
        </button>
      </div>
    </div>
  );
}
